import { QuotePaymentScheduleAmountTypeEnum } from '@/gen/cortado/v1/settings_pb'
import { formatCurrency } from '@/lib/currency-utils'
import { getQuoteFixedPaymentScheduleTotalCents, type PaymentScheduleFormValue } from '@/lib/payment-schedule-utils'

export type QuotePaymentScheduleValidationIssue = {
  kind: 'mixed' | 'percentage' | 'fixed'
  message: string
}

/**
 * Why a quote's payment schedule can't be saved against `quoteTotalCents`
 * (the tip-exclusive total), or `undefined` when it can. Schedules are all
 * percentage (summing to 100%) or all fixed (summing to the total); none at
 * all is valid.
 */
export function getQuotePaymentScheduleValidationIssue(
  paymentSchedules: PaymentScheduleFormValue[],
  quoteTotalCents: number,
): QuotePaymentScheduleValidationIssue | undefined {
  if (paymentSchedules.length === 0) return undefined

  const hasPercentage = paymentSchedules.some(
    (schedule) =>
      schedule.amountType === QuotePaymentScheduleAmountTypeEnum.QUOTE_PAYMENT_SCHEDULE_AMOUNT_TYPE_PERCENTAGE,
  )
  const hasFixed = paymentSchedules.some(
    (schedule) =>
      schedule.amountType === QuotePaymentScheduleAmountTypeEnum.QUOTE_PAYMENT_SCHEDULE_AMOUNT_TYPE_FIXED,
  )

  if (hasPercentage && hasFixed) {
    return {
      kind: 'mixed',
      message: 'Cannot mix percentage and fixed amount payment schedules.',
    }
  }

  if (hasPercentage) {
    const percentTotal = paymentSchedules.reduce((sum, schedule) => sum + (schedule.percentage || 0), 0)
    if (Math.abs(percentTotal - 100) > 0.01) {
      return {
        kind: 'percentage',
        message: `Percentages must total 100% (currently ${percentTotal.toFixed(1)}%).`,
      }
    }
    return undefined
  }

  const fixedTotalCents = getQuoteFixedPaymentScheduleTotalCents(paymentSchedules)
  if (fixedTotalCents === undefined || fixedTotalCents === quoteTotalCents) {
    return undefined
  }

  return {
    kind: 'fixed',
    message: fixedTotalCents < quoteTotalCents
      ? `Payment schedule total ($${formatCurrency(fixedTotalCents)}) is less than quote total ($${formatCurrency(quoteTotalCents)}).`
      : `Payment schedule total ($${formatCurrency(fixedTotalCents)}) exceeds quote total ($${formatCurrency(quoteTotalCents)}).`,
  }
}
